import L from 'leaflet'
import { memo, useRef, useState } from 'react'

import Loading from '@/components/loading'
import { Map } from '@/components/map'
import { CenterToBounds } from '@/components/map/center-to-bounds'
import {
  LayersConfig,
  LayersViewControl,
} from '@/components/map/layers-view-control'
import { GeoJSONObject } from '@/types/geospatial'

import { useManageLayersVisibility } from '../../../hooks/use-manage-layers-visibility'
import { useRegionContext } from '../contexts/region-context'
import { useMultipleFieldSelection } from '../hooks/use-multiple-field-selection'
import { LegendsBox } from './components/legends-box'
import { MapTools } from './components/map-tools'
import { REGIONS_LAYERS_CONFIG } from './config/map-layers-config'
import FieldsNamesLayer from './map-layers/fields-names'
import PerimetersLayer from './map-layers/fields-perimeter'
import { useStyles } from './styles'

export type MapMode = 'navigation' | 'multipleSelection'

const RegionMapComponent = () => {
  const classes = useStyles()

  const [mapMode, setMapMode] = useState<MapMode>('navigation')

  const mapRef = useRef<L.Map | null>(null)

  const { layersVisibility, handleLayerChange } =
    useManageLayersVisibility<LayersConfig>(REGIONS_LAYERS_CONFIG)

  const {
    regionData,
    isLoadingRegion,
    regionFieldsInfo,
    currentViewBounds,
    multipleFieldsSelection,
    toggleFieldSelection,
  } = useRegionContext()

  const fieldColunmName = regionData?.col_name
  const geoJsonData = regionData?.geojson as GeoJSONObject
  const bounds = geoJsonData?.features?.length
    ? L.geoJSON(geoJsonData).getBounds().pad(0.2)
    : null
  const features = geoJsonData?.features || []

  useMultipleFieldSelection({
    mapRef,
    mapMode,
    features,
    regionFieldsInfo,
    isPerimetersVisible: layersVisibility.perimeters.checked,
    onMultipleFieldsSelection: multipleFieldsSelection,
  })

  if (isLoadingRegion) {
    return (
      <div className={classes.loadingContainer}>
        <Loading />
      </div>
    )
  }

  const defaultBounds = L.latLngBounds([-22.0, -58.0], [-19.0, -53.0])
  const isDefaultMap = !bounds || !features.length

  return (
    <Map
      bounds={isDefaultMap ? defaultBounds : bounds!}
      maxBounds={isDefaultMap ? defaultBounds : bounds!}
      preferCanvas
      disabledControls={{
        baseLayerControl: true,
        centralizationControl: true,
        doubleClickZoom: true,
        attributionControl: true,
      }}
      positionControls={{
        zoomControl: 'topright',
        scaleControl: 'topleft',
        attributionControl: 'bottomleft',
      }}
      whenCreated={(map) => {
        mapRef.current = map
        map.invalidateSize()
      }}
      zoomSnap={0.25}
    >
      <LegendsBox />
      <LayersViewControl
        layersConfig={layersVisibility}
        onLayerChange={handleLayerChange}
        title={'Selecione o que deseja ver'}
      />

      <PerimetersLayer
        features={features as GeoJSON.Feature[]}
        regionFieldsInfo={regionFieldsInfo}
        onToggleFieldSelection={toggleFieldSelection}
        visibility={layersVisibility.perimeters.checked}
      />

      <FieldsNamesLayer
        features={features as GeoJSON.Feature[]}
        visibility={layersVisibility.fieldNames.checked}
        fieldColunmName={fieldColunmName!}
      />
      <CenterToBounds bounds={currentViewBounds} />

      <MapTools
        mapRef={mapRef}
        mapMode={mapMode}
        setMapMode={setMapMode}
        bounds={bounds}
      />
    </Map>
  )
}

export const RegionMap = memo(RegionMapComponent)
